/**
 * İşçilik alacakları hesabını PDF'e döker.
 *
 * TÜRKÇE KARAKTER NOTU
 * --------------------
 * jsPDF'in gömülü fontları WinAnsi (cp1252) kodlamasını kullanır.
 * Bu kodlama ç ö ü Ç Ö Ü karakterlerini İÇERİR ama ğ ş İ ı
 * karakterlerini İÇERMEZ; onlar bozuk çıkar.
 *
 * Çözüm sırası:
 *  1) public/fonts/ altında bir Unicode TTF varsa onu gömer
 *     (tam Türkçe desteği).
 *  2) Yoksa yalnızca desteklenmeyen 4 karakteri en yakın
 *     karşılığına çevirir; ç/ö/ü doğru kalır.
 *
 * Tam Türkçe için: lisansına sahip olduğunuz bir TTF'yi
 * public/fonts/rapor.ttf olarak koymanız yeterli.
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatTL } from './iscilikAlacaklari';

const UNICODE_FONT_URL = '/fonts/rapor.ttf';
let fontDurumu = null; // null = denenmedi, 'var' | 'yok'

/** WinAnsi'de bulunmayan karakterleri yedekle. */
const winAnsiGuvenli = (s) =>
  String(s ?? '')
    .replace(/ğ/g, 'g').replace(/Ğ/g, 'G')
    .replace(/ş/g, 's').replace(/Ş/g, 'S')
    .replace(/İ/g, 'I').replace(/ı/g, 'i');

/**
 * Gelen baytlar gerçekten TTF/OTF mi?
 *
 * HATA DÜZELTMESİ (2026-09-04): Font dosyası YOKKEN bile sunucu 404
 * değil, SPA yönlendirmesi nedeniyle 200 + index.html döndürüyordu.
 * "res.ok" kontrolü bunu geçiyor, HTML font olarak kaydediliyor ve
 * jsPDF "Cannot read properties of undefined (reading 'widths')"
 * hatasıyla çöküyordu. Artık imza baytları doğrulanıyor.
 */
function gercekFontMu(bytes, contentType) {
  if (!bytes || bytes.length < 4) return false;
  if (contentType && /text\/html/i.test(contentType)) return false;
  const [a, b, c, d] = bytes;
  const etiket = String.fromCharCode(a, b, c, d);
  return (
    (a === 0x00 && b === 0x01 && c === 0x00 && d === 0x00) || // TrueType
    etiket === 'OTTO' ||                                       // CFF / OpenType
    etiket === 'true' || etiket === 'ttcf'                     // Apple / koleksiyon
  );
}

async function fontYukle(doc) {
  if (fontDurumu === 'yok') return false;
  try {
    const res = await fetch(UNICODE_FONT_URL);
    if (!res.ok) { fontDurumu = 'yok'; return false; }

    const buf = await res.arrayBuffer();
    const bytes = new Uint8Array(buf);

    if (!gercekFontMu(bytes, res.headers.get('content-type'))) {
      // Font yok; sunucu SPA sayfasını döndürmüş olabilir.
      fontDurumu = 'yok';
      return false;
    }

    let bin = '';
    const chunk = 8192;
    for (let i = 0; i < bytes.length; i += chunk) {
      bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    const b64 = btoa(bin);
    doc.addFileToVFS('rapor.ttf', b64);
    doc.addFont('rapor.ttf', 'rapor', 'normal');
    doc.setFont('rapor');
    // Fontun gerçekten kullanılabildiğini doğrula
    doc.getTextWidth('İşçilik');
    fontDurumu = 'var';
    return true;
  } catch {
    // Yarım kalmış font kaydı varsa güvenli fonta dön
    try { doc.setFont('helvetica'); } catch { /* yoksay */ }
    fontDurumu = 'yok';
    return false;
  }
}

/**
 * @param {object} sonuc  hesaplaTumAlacaklar çıktısı
 * @param {object} bilgi  { muvekkil, foyNo, isveren, giris, cikis, ciplak, giydirilmis }
 * @returns {Promise<{blob: Blob, dosyaAdi: string, unicode: boolean}>}
 */
export async function iscilikPdfUret(sonuc, bilgi = {}) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const unicode = await fontYukle(doc);
  const T = unicode ? ((s) => String(s ?? '')) : winAnsiGuvenli;

  const tarihTR = (d) => {
    if (!d) return '-';
    const s = String(d).slice(0, 10);
    const [y, m, g] = s.split('-');
    return y ? `${g}.${m}.${y}` : s;
  };

  let y = 16;
  doc.setFontSize(15);
  doc.text(T('İŞÇİLİK ALACAKLARI HESAP TABLOSU'), 14, y);

  y += 7;
  doc.setFontSize(9);
  doc.setTextColor(110);
  doc.text(
    T(`Düzenleme tarihi: ${new Date().toLocaleDateString('tr-TR')}`),
    14, y
  );
  doc.setTextColor(0);

  // ---- Taraf / dosya bilgileri ----
  const bilgiSatirlari = [
    ['Föy No', bilgi.foyNo ? String(bilgi.foyNo) : '-'],
    ['Müvekkil', bilgi.muvekkil || '-'],
    ['İşveren', bilgi.isveren || '-'],
    ['İşe giriş', tarihTR(bilgi.giris)],
    ['İşten çıkış', tarihTR(bilgi.cikis)],
    ['Çalışma süresi', `${sonuc?.sure?.yil ?? 0} yıl ${sonuc?.sure?.kalanGun ?? 0} gün`],
    ['Çıplak brüt ücret', `${formatTL(bilgi.ciplak)} TL`],
    ['Giydirilmiş brüt ücret', `${formatTL(bilgi.giydirilmis)} TL`],
    ['Kıdem tavanı', `${formatTL(sonuc?.tavan)} TL`],
  ];

  autoTable(doc, {
    startY: y + 4,
    theme: 'plain',
    styles: { font: unicode ? 'rapor' : 'helvetica', fontSize: 9, cellPadding: 1.6 },
    columnStyles: { 0: { cellWidth: 46, textColor: 90 }, 1: { fontStyle: 'bold' } },
    body: bilgiSatirlari.map(([k, v]) => [T(k), T(v)]),
  });

  // ---- Alacak kalemleri ----
  const kalemler = (sonuc?.kalemler || []).map((k) => [
    T(k.tur),
    T(k.aciklama || ''),
    formatTL(k.brut),
    formatTL(k.gelirVergisi || 0),
    formatTL(k.damga || 0),
    formatTL(k.net),
  ]);

  autoTable(doc, {
    startY: (doc.lastAutoTable?.finalY || y) + 6,
    head: [[T('Alacak Kalemi'), T('Açıklama'), T('Brüt'), T('Gelir V.'), T('Damga V.'), T('Net')]],
    body: kalemler,
    styles: { font: unicode ? 'rapor' : 'helvetica', fontSize: 8.5, cellPadding: 2 },
    headStyles: { fillColor: [43, 54, 82], textColor: 255, fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 34 },
      2: { halign: 'right' }, 3: { halign: 'right' },
      4: { halign: 'right' }, 5: { halign: 'right', fontStyle: 'bold' },
    },
    foot: [[
      T('TOPLAM'), '',
      formatTL(sonuc?.toplam?.brut),
      formatTL(sonuc?.toplam?.gelirVergisi),
      formatTL(sonuc?.toplam?.damga),
      formatTL(sonuc?.toplam?.net),
    ]],
    footStyles: { fillColor: [238, 242, 247], textColor: 20, fontStyle: 'bold', halign: 'right' },
  });

  // ---- Dipnot ----
  const sonY = (doc.lastAutoTable?.finalY || 200) + 8;
  doc.setFontSize(7.5);
  doc.setTextColor(120);
  const notlar = [
    'Hesaplama 2026 yılı parametrelerine göre yapılmıştır: gelir vergisi tarifesi 31.12.2025 tarihli Resmî Gazete,',
    'kıdem tazminatı tavanı ilgili dönem, SGK işçi payı %14 + işsizlik %1, damga vergisi binde 7,59.',
    'Kıdem tazminatından yalnızca damga vergisi kesilir. Bu tablo bilgilendirme amaçlıdır; resmî bilirkişi raporu yerine geçmez.',
  ];
  notlar.forEach((n, i) => doc.text(T(n), 14, sonY + i * 4));

  if (!unicode) {
    doc.setTextColor(150);
    doc.text(
      T('Not: Unicode font bulunamadigi icin bazi Turkce karakterler sadelestirilmistir.'),
      14, sonY + notlar.length * 4 + 4
    );
  }

  const ad = `iscilik-alacaklari-${bilgi.foyNo || 'hesap'}-${new Date().toISOString().slice(0, 10)}.pdf`;
  return { blob: doc.output('blob'), dosyaAdi: ad, unicode };
}
